import React, { useState, useRef, useEffect } from "react";

/**
 * CustomSelect - Dropdown personalizado compatible con navegadores antiguos
 *
 * Props:
 * - value: valor seleccionado
 * - options: array de { value, text }
 * - onChange: función callback (recibe el value)
 * - placeholder: texto cuando no hay selección
 * - disabled: deshabilitar el select
 * - hasError: mostrar estado de error
 * - name: nombre del campo (para formularios)
 */
const CustomSelect = ({
  value,
  options = [],
  onChange,
  placeholder = "Seleccione",
  disabled = false,
  hasError = false,
  name = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Encontrar el texto de la opción seleccionada
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const displayText = selectedOption ? selectedOption.text : placeholder;

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);


  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Clases CSS
  const containerClass = [
    "custom-select-container",
    disabled ? "custom-select-disabled" : "",
    hasError ? "custom-select-error shake" : "",
    isOpen ? "custom-select-open" : ""
  ].filter(Boolean).join(" ");

  return (
    <div className={containerClass} ref={containerRef}>
      {/* Botón principal del select */}
      <div
        className="custom-select-trigger"
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <span className={selectedOption ? "custom-select-value" : "custom-select-placeholder"}>
          {displayText}
        </span>
        <span className="custom-select-arrow">
          <i className={isOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
        </span>
      </div>

      {/* Dropdown de opciones */}
      {isOpen && (
        <div className="custom-select-dropdown" onKeyDown={handleKeyDown}>
          {/* Lista de opciones */}
          <ul className="custom-select-options">
            {options.length > 0 ? (
              options.map((option, index) => (
                <li
                  key={index}
                  className={`custom-select-option ${String(option.value) === String(value) ? "selected" : ""}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.text}
                </li>
              ))
            ) : (
              <li className="custom-select-no-results">Sin opciones</li>
            )}
          </ul>
        </div>
      )}

      {/* Input oculto para formularios */}
      <input type="hidden" name={name} value={value || ""} />
    </div>
  );
};

export default CustomSelect;
